import { useState, useEffect } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { type QtiQuestionDTO, QuestionRenderer } from '@/src/lib';
import { apiRequest } from '@/src/services';

interface ScoreQuestionModalProps {
    onClose: () => void;
    onSave: (isCorrect: boolean, studentUserId: number, attemptQuestionId: number) => void;
    questionId: number;
    studentUserId: number;
    isScored: boolean;
    isCorrect: boolean;
    attemptQuestionId: number;
    submittedAnswer: string;
    questionAnswer: string;
}

export default function ScoreQuestionModal({
    onClose,
    onSave,
    questionId,
    studentUserId,
    isScored,
    isCorrect,
    attemptQuestionId,
    submittedAnswer,
    questionAnswer,
}: ScoreQuestionModalProps) {
    const [questionData, setQuestionData] = useState<QtiQuestionDTO | null>(null);
    const [questionCache, setQuestionCache] = useState<Record<number, QtiQuestionDTO>>({});

    useEffect(() => {
        const fetchQuestion = async () => {
            if (questionCache[questionId]) {
                setQuestionData(questionCache[questionId]);
                return;
            }

            try {
                const res = await apiRequest<QtiQuestionDTO>(
                    'get',
                    `/assessments/questions/${questionId}`
                );
                setQuestionData(res.data);
                setQuestionCache((prev) => ({ ...prev, [questionId]: res.data }));
            } catch (err) {
                console.error('Error fetching question:', err);
                setQuestionData(null);
            }
        };

        fetchQuestion();
    }, [questionId, questionCache]);

    if (!questionData) return null;

    return (
        <Box sx={{ flex: 1, p: 3, borderLeft: '1px solid #ddd' }}>
            {isScored && (
                <div style={{ color: isCorrect ? 'green' : 'red', marginBottom: '16px' }}>
                    This question is currently marked as {isCorrect ? 'correct' : 'incorrect'}.
                </div>
            )}
            {/* Student’s Answer */}
            <Box sx={{ mb: 2 }}>
                <QuestionRenderer
                    question={questionData}
                    loadedUserResponse={submittedAnswer}
                    stimMode="sev"
                    options={{
                        hideKeypad: true,
                        disabled: true,
                        language: 'en',
                    }}
                />
                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mt: 2 }}>
                    Student’s Answer
                </Typography>
            </Box>
            <hr />
            {/* Correct Answer */}
            <Box sx={{ mt: 4, mb: 2 }}>
                <QuestionRenderer
                    question={questionData}
                    loadedUserResponse={questionAnswer}
                    stimMode="sev"
                    options={{
                        hideKeypad: true,
                        disabled: true,
                        hidePrompt: true,
                        language: 'en',
                    }}
                />
                <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="success.main"
                    sx={{ mt: 2 }}
                >
                    Correct Answer
                </Typography>
            </Box>
            <hr />
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                    variant="contained"
                    color="success"
                    disabled={isScored && isCorrect}
                    onClick={() => onSave(true, studentUserId, attemptQuestionId)}
                >
                    Correct
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    disabled={isScored && !isCorrect}
                    onClick={() => onSave(false, studentUserId, attemptQuestionId)}
                >
                    Incorrect
                </Button>
                <Button onClick={onClose}>Cancel</Button>
            </Box>
            <Box sx={{ height: 40 }} /> {/* bottom padding */}
        </Box>
    );
}
